import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AdsSummaryResponse } from '@brand24-original/llm-types';
import { TileBox } from '../../projects/preview/components/tile-box/TileBox';
import { adsTrend } from '../helpers/AdsHelper';

interface AdsTilesProps {
  summary: AdsSummaryResponse | undefined;
  /** Same summary for the preceding period of equal length — drives the trend badges. */
  previousSummary: AdsSummaryResponse | undefined;
  isLoading: boolean;
}

const percentOf = (part: number, total: number): number =>
  total > 0 ? Math.round((part / total) * 1000) / 10 : 0;

/**
 * KPI tiles. The sub-line under each value carries the secondary fact (unique creatives, % of
 * prompts, …), the (i) tooltip explains the metric, and the badge shows the change against the
 * previous period — the same layout as the Overview tiles.
 */
const AdsTiles: React.FC<AdsTilesProps> = ({ summary, previousSummary, isLoading }) => {
  const { t } = useTranslation();
  const hasData = !isLoading && !!summary;
  // A previous period with no measured answers has nothing to compare against — hide the badges.
  const previous = previousSummary && previousSummary.monitoredPrompts > 0 ? previousSummary : undefined;

  const promptsPercent = summary ? percentOf(summary.promptsWithAds, summary.monitoredPrompts) : 0;

  return (
    <div className="tiles tiles--page">
      <TileBox
        hasData={hasData}
        label="ads.adsDetected"
        tooltipText={t('ads.adsDetectedTooltip', { count: summary?.uniqueAds ?? 0 })}
        value={summary?.adOccurrences ?? 0}
        subValue={t('ads.adsDetectedSub', { count: summary?.uniqueAds ?? 0 })}
        trendValue={adsTrend(summary?.adOccurrences ?? 0, previous?.adOccurrences)}
      />
      <TileBox
        hasData={hasData}
        label="ads.promptsWithAds"
        tooltipText={t('ads.promptsWithAdsTooltip', { percent: promptsPercent })}
        value={`${summary?.promptsWithAds ?? 0} / ${summary?.monitoredPrompts ?? 0}`}
        subValue={t('ads.promptsWithAdsSub', { percent: promptsPercent })}
        trendValue={adsTrend(summary?.promptsWithAds ?? 0, previous?.promptsWithAds)}
      />
      <TileBox
        hasData={hasData}
        label="ads.advertisersDetected"
        tooltipText="ads.advertisersDetectedTooltip"
        value={summary?.advertisersDetected ?? 0}
        subValue={t('ads.advertisersDetectedSub')}
        trendValue={adsTrend(summary?.advertisersDetected ?? 0, previous?.advertisersDetected)}
      />
    </div>
  );
};

export default AdsTiles;
